import {
  SearchApiFacilityPartner,
  SearchApiFacilityResult,
  SearchApiProcedureResult,
  SearchApiProfessionalResult,
  SearchApiTermSpecialty,
} from '@/app/(main)/_components/search-modal/_services/search-api/search-api.type';
import { SearchResult } from '@/app/(main)/_components/search-modal/search-modal.types';
import { SearchType } from '@/types/most-search-type';

const CATEGORY_SEARCH_TYPE: Record<string, SearchType> = {
  LABORATORIAL_EXAM: SearchType.LABORATORIAL_EXAM,
  IMAGE_EXAM: SearchType.IMAGE_EXAM,
  VACCINE: SearchType.VACCINE,
  ONLINE_CONSULTATION: SearchType.ONLINE_CONSULTATION,
  PRESENTIAL_CONSULTATION: SearchType.PRESENTIAL_CONSULTATION,
  EMERGENCY: SearchType.EMERGENCY,
  SURGERY: SearchType.SURGERY,
};

const toSpecialtyNames = (specialties?: SearchApiTermSpecialty[]): string[] | undefined =>
  specialties?.map((specialty) => specialty.name);

export function resolveProcedureSearchType(category?: string | null): SearchType {
  return (category && CATEGORY_SEARCH_TYPE[category]) || SearchType.PROCEDURE;
}

export function mapProcedureToSearchResult(procedure: SearchApiProcedureResult): SearchResult {
  return {
    id: procedure.id,
    name: procedure.name,
    displayName: procedure.alias || procedure.name,
    code: procedure.code,
    tussCode: procedure.tussCode ?? undefined,
    slug: procedure.slug,
    description: procedure.description,
    alias: procedure.alias ?? undefined,
    keywords: procedure.keywords,
    specialties: toSpecialtyNames(procedure.specialties),
    availableForTelehealth: procedure.availableForTelehealth,
    searchType: resolveProcedureSearchType(procedure.category),
  };
}

export function mapProfessionalToSearchResult(professional: SearchApiProfessionalResult): SearchResult {
  return {
    id: professional.id,
    name: professional.name,
    displayName: professional.honorific
      ? `${professional.honorific} ${professional.name}`
      : professional.name,
    avatar: professional.avatar,
    slug: professional.slug,
    specialties: toSpecialtyNames(professional.specialties),
    searchType: SearchType.DOCTOR,
  };
}

const isLaboratoryFacility = (facility: SearchApiFacilityResult): boolean =>
  facility.partner.type === 'LABORATORY';

const resolvePartnerName = (partner: SearchApiFacilityPartner): string =>
  partner.socialName || partner.legalName || '';

const resolveFacilityDisplayName = (facility: SearchApiFacilityResult): string => {
  const partnerName = resolvePartnerName(facility.partner);

  if (!isLaboratoryFacility(facility)) {
    return partnerName;
  }

  return [partnerName, facility.name].filter(Boolean).join(' - ');
};

const mapFacilityToSearchResult = (facility: SearchApiFacilityResult): SearchResult => {
  const isLaboratory = isLaboratoryFacility(facility);

  return {
    id: isLaboratory ? facility.id : facility.partner.id,
    displayName: resolveFacilityDisplayName(facility),
    avatar: facility.avatar ?? facility.partner.avatar ?? undefined,
    slug: facility.slug,
    city: facility.city ?? undefined,
    state: facility.state ?? undefined,
    searchType: isLaboratory ? SearchType.BRANCH_LABORATORY : SearchType.CLINIC,
  };
};

export function mapFacilitiesToSearchResults(facilities: SearchApiFacilityResult[]): SearchResult[] {
  const clinicPartnersAlreadyMapped = new Set<string>();

  return facilities.flatMap((facility) => {
    if (!isLaboratoryFacility(facility)) {
      if (clinicPartnersAlreadyMapped.has(facility.partner.id)) {
        return [];
      }

      clinicPartnersAlreadyMapped.add(facility.partner.id);
    }

    return [mapFacilityToSearchResult(facility)];
  });
}
